import config from '../config';
export class ApiService {
    constructor(baseUrl = config.backendUrl) {
        this.baseUrl = baseUrl;
    }
    /**
     * Get the login URL for redirecting to OAuth2 flow
     */
    getLoginUrl(returnUrl) {
        const params = returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : '';
        return `${this.baseUrl}/auth/login${params}`;
    }
    /**
     * Check authentication status
     */
    async getAuthStatus() {
        const response = await fetch(`${this.baseUrl}/auth/status`, {
            method: 'GET',
            credentials: 'include', // Include cookies
        });
        if (!response.ok) {
            return { authenticated: false, user: null };
        }
        return response.json();
    }
    /**
     * Get current user info (requires authentication)
     */
    async getCurrentUser() {
        const response = await fetch(`${this.baseUrl}/auth/me`, {
            method: 'GET',
            credentials: 'include',
        });
        if (!response.ok) {
            return null;
        }
        return response.json();
    }
    /**
     * Log out the current user
     * Returns the IdP logout URL if available (for full SSO logout)
     */
    async logout() {
        const response = await fetch(`${this.baseUrl}/auth/logout`, {
            method: 'POST',
            credentials: 'include',
        });
        if (response.ok) {
            return response.json();
        }
        return {};
    }
    async getAvailableModels() {
        const response = await fetch(`${this.baseUrl}/api/available-models`, {
            method: 'GET',
            credentials: 'include',
        });
        if (!response.ok) {
            if (response.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            throw new Error(`Failed to fetch models: ${response.status} ${response.statusText}`);
        }
        return response.json();
    }
    async uploadImage(file) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`${this.baseUrl}/api/upload-image`, {
            method: 'POST',
            body: formData,
            credentials: 'include', // Include cookies for auth
        });
        if (!response.ok) {
            if (response.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
        }
        return response.json();
    }
    async generateMetadata(fileId, options = {}) {
        const response = await fetch(`${this.baseUrl}/api/generate-metadata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                file_id: fileId,
                instrument: options.instrument || '',
                experiment: options.experiment || '',
                nexus_definition: options.nexus_definition || '',
                sample_id: options.sample_id || '',
                model: options.model || '',
            }),
            credentials: 'include', // Include cookies for auth
        });
        if (!response.ok) {
            if (response.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            throw new Error(`Metadata generation failed: ${response.status} ${response.statusText}`);
        }
        return response.json();
    }
    async chatMapping(request) {
        const response = await fetch(`${this.baseUrl}/api/chat-mapping`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(request),
            credentials: 'include',
        });
        if (!response.ok) {
            if (response.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            const errorText = await response.text();
            throw new Error(`Chat mapping failed: ${response.status} ${response.statusText} - ${errorText}`);
        }
        return response.json();
    }
    async validateNexusFile(nexusId) {
        const response = await fetch(`${this.baseUrl}/api/validate-nexus/${nexusId}`, {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) {
            if (response.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            throw new Error(`Validation failed: ${response.status} ${response.statusText}`);
        }
        return response.json();
    }
    async downloadValidationReport(nexusId) {
        const response = await fetch(`${this.baseUrl}/api/validation-report/${nexusId}`, {
            method: 'GET',
            credentials: 'include',
        });
        if (!response.ok) {
            if (response.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            throw new Error(`Download failed: ${response.status} ${response.statusText}`);
        }
        return response.blob();
    }
    async downloadNexusFile(nexusId) {
        const url = `${this.baseUrl}/api/download-nexus/${nexusId}`;
        try {
            const response = await fetch(url, {
                method: 'GET',
                mode: 'cors',
                credentials: 'include',
            });
            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Authentication required. Please log in.');
                }
                const errorText = await response.text();
                console.error('Download error:', response.status, errorText);
                if (response.status === 0) {
                    throw new Error('CORS error: Request blocked by browser. Check if backend allows the frontend origin.');
                }
                throw new Error(`Download failed: ${response.status} ${response.statusText} - ${errorText}`);
            }
            return await response.blob();
        }
        catch (error) {
            console.error('Download fetch error:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            if (errorMessage.includes('CORS') || errorMessage.includes('cross-origin')) {
                throw new Error('CORS error: Browser blocked the request. Backend may not allow requests from this origin.');
            }
            if (errorMessage.includes('Mixed Content') || errorMessage.includes('HTTPS')) {
                throw new Error('Mixed content error: Frontend is HTTPS but backend is HTTP. Configure both to use the same protocol.');
            }
            throw error;
        }
    }
}
export const apiService = new ApiService(config.backendUrl);
